import {
  trigger,
  state,
  style,
  transition,
  animate,
  keyframes,
  AnimationTriggerMetadata,
} from '@angular/animations';

/**
 * Smooth collapse animation for Angular elements
 * Animates height, opacity, and padding for a natural collapse effect
 */
export const collapseAnimation: AnimationTriggerMetadata = trigger('collapse', [
  state(
    'expanded',
    style({
      height: '*',
      opacity: 1,
      paddingTop: '*',
      paddingBottom: '*',
      marginTop: '*',
      marginBottom: '*',
      overflow: 'visible',
    })
  ),
  state(
    'collapsed',
    style({
      height: '0px',
      opacity: 0,
      paddingTop: '0px',
      paddingBottom: '0px',
      marginTop: '0px',
      marginBottom: '0px',
      overflow: 'hidden',
    })
  ),
  transition('expanded => collapsed', [
    style({ overflow: 'hidden' }),
    animate(
      '300ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      keyframes([
        style({
          height: '*',
          opacity: 1,
          paddingTop: '*',
          paddingBottom: '*',
          marginTop: '*',
          marginBottom: '*',
          offset: 0,
        }),
        style({
          height: '*',
          opacity: 0.7,
          paddingTop: '*',
          paddingBottom: '*',
          marginTop: '*',
          marginBottom: '*',
          offset: 0.3,
        }),
        style({
          height: '0px',
          opacity: 0,
          paddingTop: '0px',
          paddingBottom: '0px',
          marginTop: '0px',
          marginBottom: '0px',
          offset: 1,
        }),
      ])
    ),
  ]),
  transition('collapsed => expanded', [
    style({ overflow: 'hidden' }),
    animate(
      '350ms cubic-bezier(0.0, 0.0, 0.2, 1)',
      keyframes([
        style({
          height: '0px',
          opacity: 0,
          paddingTop: '0px',
          paddingBottom: '0px',
          marginTop: '0px',
          marginBottom: '0px',
          offset: 0,
        }),
        style({
          height: '*',
          opacity: 0.3,
          paddingTop: '*',
          paddingBottom: '*',
          marginTop: '*',
          marginBottom: '*',
          offset: 0.7,
        }),
        style({
          height: '*',
          opacity: 1,
          paddingTop: '*',
          paddingBottom: '*',
          marginTop: '*',
          marginBottom: '*',
          overflow: 'visible',
          offset: 1,
        }),
      ])
    ),
  ]),
]);

/**
 * Fast collapse animation for quick interactions
 * Shorter duration for rapid expand/collapse scenarios
 */
export const fastCollapseAnimation: AnimationTriggerMetadata = trigger('fastCollapse', [
  state(
    'expanded',
    style({
      height: '*',
      opacity: 1,
      overflow: 'visible',
    })
  ),
  state(
    'collapsed',
    style({
      height: '0px',
      opacity: 0,
      overflow: 'hidden',
    })
  ),
  transition('expanded => collapsed', [
    style({ overflow: 'hidden' }),
    animate('200ms ease-out'),
  ]),
  transition('collapsed => expanded', [
    style({ overflow: 'hidden' }),
    animate('250ms ease-in'),
  ]),
]);

/**
 * Slide collapse animation with transform
 * Uses transform for better performance on mobile devices
 */
export const slideCollapseAnimation: AnimationTriggerMetadata = trigger('slideCollapse', [
  state(
    'expanded',
    style({
      height: '*',
      opacity: 1,
      transform: 'translateY(0)',
      overflow: 'visible',
    })
  ),
  state(
    'collapsed',
    style({
      height: '0px',
      opacity: 0,
      transform: 'translateY(-10px)',
      overflow: 'hidden',
    })
  ),
  transition('expanded => collapsed', [
    style({ overflow: 'hidden' }),
    animate('280ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'),
  ]),
  transition('collapsed => expanded', [
    style({ overflow: 'hidden' }),
    animate('320ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'),
  ]),
]);

/**
 * Accordion-style collapse animation
 * Designed for accordion components with smooth easing
 */
export const accordionCollapseAnimation: AnimationTriggerMetadata = trigger('accordionCollapse', [
  state(
    'expanded',
    style({
      height: '*',
      opacity: 1,
      paddingTop: '*',
      paddingBottom: '*',
    })
  ),
  state(
    'collapsed',
    style({
      height: '0px',
      opacity: 0,
      paddingTop: '0px',
      paddingBottom: '0px',
    })
  ),
  transition('expanded <=> collapsed', [
    style({ overflow: 'hidden' }),
    animate('400ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
  ]),
]);

/**
 * Utility function to create custom collapse animation with configurable duration
 * @param duration - Animation duration in milliseconds (default: 300)
 * @param easing - CSS easing function (default: 'cubic-bezier(0.4, 0.0, 0.2, 1)')
 */
export function createCollapseAnimation(
  duration: number = 300,
  easing: string = 'cubic-bezier(0.4, 0.0, 0.2, 1)'
): AnimationTriggerMetadata {
  return trigger('customCollapse', [
    state(
      'expanded',
      style({
        height: '*',
        opacity: 1,
        overflow: 'visible',
      })
    ),
    state(
      'collapsed',
      style({
        height: '0px',
        opacity: 0,
        overflow: 'hidden',
      })
    ),
    transition('expanded => collapsed', [
      style({ overflow: 'hidden' }),
      animate(`${duration}ms ${easing}`),
    ]),
    transition('collapsed => expanded', [
      style({ overflow: 'hidden' }),
      animate(`${Math.round(duration * 1.1)}ms ${easing}`),
    ]),
  ]);
}
